import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import type { InitializeMode, PersistenceLevel } from '@/core/flux.service';
import type { ExploreWorkbookModalState, Folder, Item, SearchParams } from '@/explorer/exploreModal.types';
import type { SearchItemLocation } from '@/main/app.constants';
import type { RootEnum } from '@seeqdev/api/endpoint/FoldersApi';

export type ExploreWorkbookModalDehydratedState = Pick<ExploreWorkbookModalState, 'pageSize' | 'initialFolderId'>;

export class ExploreWorkbookModalStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqExploreWorkbookModalStore';

  initialize(initializeMode?: InitializeMode): void {
    const saveState = this.state && initializeMode !== 'FORCE';
    const initialState: ExploreWorkbookModalState = {
      initialFolderId: undefined,
      tableFilter: saveState ? this.state.get('tableFilter') : SEARCH_ITEM_LOCATIONS.RECENT,
      itemTotalForTable: saveState ? this.state.get('itemTotalForTable') : 0,
      pageSize: saveState ? this.state.get('pageSize') : 5,
      pageNumber: saveState ? this.state.get('pageNumber') : 1,
      searchParams: saveState ? this.state.get('searchParams') : { types: ['Folder', 'Analysis'] },
      items: saveState ? this.state.get('items') : [],
      loading: saveState ? this.state.get('loading') : false,
      folder: saveState ? this.state.get('folder') : undefined,
      sortAscending: saveState ? this.state.get('sortAscending') : false,
      sortProperty: saveState ? this.state.get('sortProperty') : 'openedAt',
      showResults: saveState ? this.state.get('showResults') : false,
      workbookId: undefined,
      workbookType: undefined,
      tableRoot: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get initialFolderId(): string | undefined {
    return this.state.get('initialFolderId');
  }

  get workbookId(): string | undefined {
    return this.state.get('workbookId');
  }

  get workbookType(): string | undefined {
    return this.state.get('workbookType');
  }

  get tableFilter(): SearchItemLocation {
    return this.state.get('tableFilter');
  }

  get searchParams(): Partial<SearchParams> {
    return this.state.get('searchParams');
  }

  get items(): Item[] {
    return this.state.get('items');
  }

  get loading(): boolean {
    return this.state.get('loading');
  }

  get folder(): Folder | undefined {
    return this.state.get('folder');
  }

  get sortAscending(): boolean {
    return this.state.get('sortAscending');
  }

  get sortProperty(): string {
    return this.state.get('sortProperty');
  }

  get showResults(): boolean {
    return this.state.get('showResults');
  }

  get tableRoot(): RootEnum | undefined {
    return this.state.get('tableRoot');
  }

  getItemTotalForTable(): number {
    return this.state.get('itemTotalForTable');
  }

  getPageSizeByTable(): number {
    return this.state.get('pageSize');
  }

  getPageNumberForTable(): number {
    return this.state.get('pageNumber');
  }

  dehydrate(): ExploreWorkbookModalDehydratedState {
    return _.omit(this.state.serialize(), [
      'effectivePermissions',
      'workbookId',
      'workbookType',
      'searchParams',
      'items',
      'folder',
      'tableFilter',
      'itemTotalForTable',
      'pageNumber',
      'sortAscending',
      'sortProperty',
      'loading',
      'showResults',
      'tableRoot',
    ]);
  }

  rehydrate(dehydratedState: Partial<ExploreWorkbookModalDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    /**
     * Clears the state for the currently loaded seeq content image.
     */
    EXPLORE_WORKBOOK_MODAL_CLEAR: () => {
      this.initialize();
    },

    EXPLORE_WORKBOOK_MODAL_SET_INITIAL_FOLDER_ID: (payload: { initialFolderId?: string }) => {
      this.state.set('initialFolderId', payload.initialFolderId);
    },

    /**
     * Sets the workbook ID
     *
     * @param payload - Object container
     * @param payload.workbookId - a workbook ID
     */
    EXPLORE_WORKBOOK_MODAL_SET_WORKBOOK_ID: (payload: { workbookId?: string; workbookType?: string }) => {
      this.state.set('workbookId', payload.workbookId);
      this.state.set('workbookType', payload.workbookType);
    },

    EXPLORE_WORKBOOK_MODAL_SET_TABLE_FILTER: (payload: { tableFilter: SearchItemLocation }) => {
      this.state.set('tableFilter', payload.tableFilter);
    },

    EXPLORE_WORKBOOK_MODAL_SET_ITEM_TOTAL_FOR_TABLE: (payload: { itemTotalForTable: number }) => {
      this.state.set('itemTotalForTable', payload.itemTotalForTable);
    },

    EXPLORE_WORKBOOK_MODAL_SET_PAGE_SIZE: (payload: { pageSize: number }) => {
      this.state.set('pageSize', payload.pageSize);
    },

    EXPLORE_WORKBOOK_MODAL_SET_PAGE_NUMBER: (payload: { pageNumber: number }) => {
      this.state.set('pageNumber', payload.pageNumber);
    },

    EXPLORE_WORKBOOK_MODAL_SET_ITEMS: (payload: { items: { id: string; name: string; type: string }[] }) => {
      this.state.set('items', payload.items);
    },

    EXPLORE_WORKBOOK_MODAL_SET_SEARCH_PARAMS: ({ field, value }: { field: string; value: string }) => {
      const searchParams = this.state.get('searchParams');

      if (value === '') {
        this.state.set('searchParams', _.omit(searchParams, field));
      } else {
        this.state.set('searchParams', _.assign({}, searchParams, { [field]: value }));
      }
    },

    EXPLORE_WORKBOOK_MODAL_SET_LOADING: (payload: { loading: boolean }) => {
      this.state.set('loading', payload.loading);
    },

    EXPLORE_WORKBOOK_MODAL_SET_FOLDER: (payload: { folder?: Folder }) => {
      this.state.set('folder', payload.folder);
    },

    EXPLORE_WORKBOOK_MODAL_SET_SORT: (payload: { sortAscending: boolean; sortProperty: string }) => {
      this.state.merge(_.pick(payload, ['sortAscending', 'sortProperty']));
    },

    EXPLORE_WORKBOOK_MODAL_SET_SHOW_RESULTS: (payload: { showResults: boolean }) => {
      this.state.set('showResults', payload.showResults);
    },

    EXPLORE_WORKBOOK_MODAL_SET_TABLE_ROOT: (payload: { root?: string }) => {
      this.state.set('tableRoot', payload.root);
    },
  };
}
